/**
 * Estado de filtros da aba Campanhas do Dashboard.
 */

import { getSalesStatusOptions } from "./dashboardSalesFilterState";

export const EMPTY_CAMPAIGN_FILTER_STATE = {
  ids_status: [],
  ids_origens: [],
  ids_campaigns: [],
  ids_whatsapp_campaigns: [],
  /** Cache de labels para chips (id → nome). */
  labels: {
    statuses: {},
    origins: {},
    campaigns: {},
    whatsappCampaigns: {},
  },
};

export function getCampaignStatusOptions() {
  return getSalesStatusOptions();
}

function toPositiveIntArray(raw) {
  const list = Array.isArray(raw) ? raw : raw != null && raw !== "" ? String(raw).split(",") : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const n = Number(String(item ?? "").trim());
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

function toStatusIntArray(raw) {
  const list = Array.isArray(raw) ? raw : raw != null && raw !== "" ? String(raw).split(",") : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const n = Number(String(item ?? "").trim());
    if (!Number.isInteger(n) || n < 0 || n > 5 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

function normalizeLabels(map) {
  if (!map || typeof map !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(map)) {
    const id = String(k).trim();
    const label = String(v ?? "").trim();
    if (!id || !label) continue;
    out[id] = label;
  }
  return out;
}

export function normalizeCampaignFilterState(raw) {
  const s = raw && typeof raw === "object" ? raw : {};
  const labels = s.labels && typeof s.labels === "object" ? s.labels : {};
  return {
    ids_status: toStatusIntArray(s.ids_status),
    ids_origens: toPositiveIntArray(s.ids_origens),
    ids_campaigns: toPositiveIntArray(s.ids_campaigns),
    ids_whatsapp_campaigns: toPositiveIntArray(s.ids_whatsapp_campaigns),
    labels: {
      statuses: normalizeLabels(labels.statuses),
      origins: normalizeLabels(labels.origins),
      campaigns: normalizeLabels(labels.campaigns),
      whatsappCampaigns: normalizeLabels(labels.whatsappCampaigns),
    },
  };
}

export function countCampaignFilterCriteria(state) {
  const s = normalizeCampaignFilterState(state);
  let n = 0;
  if (s.ids_status.length) n += 1;
  if (s.ids_origens.length) n += 1;
  if (s.ids_campaigns.length || s.ids_whatsapp_campaigns.length) n += 1;
  return n;
}

export function campaignFilterStateToQueryParams(state) {
  const s = normalizeCampaignFilterState(state);
  const params = {};
  if (s.ids_status.length) params.ids_status = s.ids_status;
  if (s.ids_origens.length) params.ids_origens = s.ids_origens;
  if (s.ids_campaigns.length) params.ids_campaigns = s.ids_campaigns;
  if (s.ids_whatsapp_campaigns.length) params.ids_whatsapp_campaigns = s.ids_whatsapp_campaigns;
  return params;
}

/** Serializa arrays para query string CSV. */
export function appendCampaignFilterQuery(qs, state) {
  const params = campaignFilterStateToQueryParams(state);
  const parts = [qs].filter(Boolean);
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value) && value.length) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.join(","))}`);
    }
  }
  return parts.join("&");
}

export function buildCampaignFilterSummaries(state, t) {
  const s = normalizeCampaignFilterState(state);
  const items = [];
  const tr = (key, fallback, vars) =>
    t(`dashboardPage.campaignFilters.${key}`, { defaultValue: fallback, ...vars });
  const statusOptions = getCampaignStatusOptions();

  s.ids_status.forEach((id) => {
    const fallback = statusOptions.find((o) => o.id === id)?.label || `#${id}`;
    const label = s.labels.statuses[String(id)] || fallback;
    items.push({
      key: `status:${id}`,
      label,
      title: tr("chipStatusTooltip", "Status: {{value}}", { value: label }),
      tone: "emphasis",
    });
  });

  s.ids_origens.forEach((id) => {
    const label = s.labels.origins[String(id)] || `#${id}`;
    items.push({
      key: `origin:${id}`,
      label,
      title: tr("chipOriginTooltip", "Canal: {{value}}", { value: label }),
      tone: "emphasis",
    });
  });

  s.ids_campaigns.forEach((id) => {
    const label = s.labels.campaigns[String(id)] || `#${id}`;
    items.push({
      key: `campaign:${id}`,
      label,
      title: tr("chipCampaignTooltip", "Campanha: {{value}}", { value: label }),
      tone: "emphasis",
    });
  });

  s.ids_whatsapp_campaigns.forEach((id) => {
    const label = s.labels.whatsappCampaigns[String(id)] || `#${id}`;
    items.push({
      key: `wa:${id}`,
      label,
      title: tr("chipWhatsappCampaignTooltip", "Campanha WhatsApp: {{value}}", { value: label }),
    });
  });

  return items;
}
